/**
 * Portfolio Page
 * --------------
 * Full research project grid with:
 *   - Page header with summary stats
 *   - Filter tabs by category (All, Biology, Chemistry, etc.)
 *   - Animated project grid — updates when filter changes
 *   - Status legend
 */
import { useState, useMemo } from 'react';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import { projects, categories } from '../../data/projects';
import styles from './Portfolio.module.css';

/* Count projects per status for the summary bar */
const liveCount      = projects.filter(p => p.status === 'Live').length;
const completedCount = projects.filter(p => p.status === 'Completed').length;
const pausedCount    = projects.filter(p => p.status === 'Paused').length;

const Portfolio = () => {
  /* Active category filter — defaults to 'All' */
  const [activeCategory, setActiveCategory] = useState('All');

  /* Filter projects whenever activeCategory changes */
  const filteredProjects = useMemo(() => {
    if (activeCategory === 'All') return projects;
    return projects.filter(p => p.category === activeCategory);
  }, [activeCategory]);

  return (
    <main className={styles.main}>

      {/* ══════════════════════════════════════
          PAGE HEADER
      ══════════════════════════════════════ */}
      <section className={styles.pageHeader}>
        <div className={styles.headerGlow} aria-hidden="true" />
        <div className={styles.container}>
          <AnimatedSection className={styles.headerContent}>
            <span className={styles.breadcrumb}>Open research</span>
            <h1 className={styles.pageTitle}>
              Projects documented in{' '}
              <span className={styles.titleAccent}>real time</span>
            </h1>
            <p className={styles.pageIntro}>
              Every project on Labora is public from day one. Browse ongoing studies, completed research, and everything in between. Follow a project, leave a comment, or reach out to collaborate.
            </p>

            {/* Status summary bar */}
            <div className={styles.statusBar}>
              <div className={styles.statusItem}>
                <span className={`${styles.statusDot} ${styles.dotLive}`} />
                <span className={styles.statusCount}>{liveCount}</span>
                <span className={styles.statusLabel}>Live now</span>
              </div>
              <div className={styles.statusDivider} />
              <div className={styles.statusItem}>
                <span className={`${styles.statusDot} ${styles.dotCompleted}`} />
                <span className={styles.statusCount}>{completedCount}</span>
                <span className={styles.statusLabel}>Completed</span>
              </div>
              <div className={styles.statusDivider} />
              <div className={styles.statusItem}>
                <span className={`${styles.statusDot} ${styles.dotPaused}`} />
                <span className={styles.statusCount}>{pausedCount}</span>
                <span className={styles.statusLabel}>Paused</span>
              </div>
              <div className={styles.statusDivider} />
              <div className={styles.statusItem}>
                <span className={styles.statusCount}>{projects.length}</span>
                <span className={styles.statusLabel}>Total projects</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FILTER + GRID
      ══════════════════════════════════════ */}
      <section className={styles.gridSection}>
        <div className={styles.container}>

          {/* Category filter tabs */}
          <AnimatedSection className={styles.filters}>
            <div className={styles.filterScroll}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ''}`}
                  onClick={() => setActiveCategory(cat)}
                  aria-pressed={activeCategory === cat}
                >
                  {cat}
                  {cat !== 'All' && (
                    <span className={styles.filterCount}>
                      {projects.filter(p => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* Result count */}
          <div className={styles.resultMeta}>
            <span className={styles.resultCount}>
              {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
              {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            </span>
          </div>

          {/* Project grid */}
          {filteredProjects.length > 0 ? (
            <div className={styles.projectGrid}>
              {filteredProjects.map((project, i) => (
                <AnimatedSection
                  key={project.id}
                  delay={Math.min(i * 60, 300)}
                  className={styles.gridItem}
                >
                  <ProjectCard project={project} />
                </AnimatedSection>
              ))}
            </div>
          ) : (
            /* Empty state when filter yields no results */
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔭</div>
              <h3 className={styles.emptyTitle}>No projects in this category yet</h3>
              <p className={styles.emptyText}>Be the first to document research in this field.</p>
            </div>
          )}

        </div>
      </section>

      {/* ══════════════════════════════════════
          BOTTOM CTA
      ══════════════════════════════════════ */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <AnimatedSection className={styles.ctaCard}>
            <div className={styles.ctaLeft}>
              <h2 className={styles.ctaTitle}>Start your own research project</h2>
              <p className={styles.ctaText}>
                Your work belongs here too. Create a public project, post your first update, and join 2,847 scientists who are documenting science in real time.
              </p>
            </div>
            <a href="/" className={styles.ctaBtn}>
              Open a project
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </AnimatedSection>
        </div>
      </section>

    </main>
  );
};

export default Portfolio;
